import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, ImageIcon, Loader2, Plus, Trash2, Upload } from "lucide-react";
import {
  listImageQuestions,
  updateQuestionImageOptions,
  type ImageQuestion,
} from "@/lib/questionnaire-assets.functions";
import { supabase } from "@/integrations/supabase/client";
import { QUESTIONNAIRE_ASSET_BUCKET } from "@/components/cockpit/questionnaires/ImageOptionFields";

export const Route = createFileRoute("/_admin/admin/questionnaires/bilder")({
  head: () => ({
    meta: [
      { title: "Bild-Optionen – Fragebögen – Admin – creategy" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ImageOptionsPage,
});

type Option = ImageQuestion["options"][number];

const MAX_BYTES = 5 * 1024 * 1024;

function ImageOptionsPage() {
  const listFn = useServerFn(listImageQuestions);
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "questionnaire-image-questions"],
    queryFn: () => listFn({ data: {} as never }),
  });

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-6">
      <header className="mb-6">
        <h1 className="font-display text-2xl font-extrabold text-primary">Bild-Optionen</h1>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          Bilder, Titel, Beschreibungstexte und Reihenfolge der Bild-Fragen. Bilder liegen im
          geschützten Bereich und werden Kundinnen und Kunden nur über signierte Links
          ausgeliefert. Ohne Bild zeigt das Cockpit eine neutrale Platzhalter-Kachel.
        </p>
      </header>

      {isLoading && (
        <p className="inline-flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> lade…
        </p>
      )}

      {!isLoading && (data ?? []).length === 0 && (
        <p className="rounded-lg border border-border bg-card p-5 text-sm text-muted-foreground">
          Aktuell nutzt keine Frage einen Bild-Feldtyp.
        </p>
      )}

      <div className="space-y-6">
        {(data ?? []).map((q) => (
          <QuestionCard key={q.id} question={q} />
        ))}
      </div>
    </div>
  );
}

function QuestionCard({ question }: { question: ImageQuestion }) {
  const queryClient = useQueryClient();
  const saveFn = useServerFn(updateQuestionImageOptions);
  const [options, setOptions] = useState<Option[]>(question.options);
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState<string | null>(null);

  useEffect(() => setOptions(question.options), [question.options]);

  const paths = useMemo(
    () => options.map((o) => o.image_path).filter((p): p is string => !!p),
    [options],
  );

  useEffect(() => {
    if (paths.length === 0) return;
    let active = true;
    void supabase.storage
      .from(QUESTIONNAIRE_ASSET_BUCKET)
      .createSignedUrls(paths, 3600)
      .then(({ data }) => {
        if (!active || !data) return;
        const next: Record<string, string> = {};
        data.forEach((entry, i) => {
          const path = paths[i];
          if (path && entry.signedUrl) next[path] = entry.signedUrl;
        });
        setUrls((prev) => ({ ...prev, ...next }));
      });
    return () => {
      active = false;
    };
  }, [paths.join("|")]);

  const saveM = useMutation({
    mutationFn: (next: Option[]) =>
      saveFn({
        data: {
          questionId: question.id,
          options: next.map((o, i) => ({
            value: o.value,
            label: o.label,
            description: o.description,
            image_path: o.image_path,
            sort_order: i,
          })),
        },
      }),
    onSuccess: () => {
      toast.success("Bild-Optionen gespeichert.");
      void queryClient.invalidateQueries({ queryKey: ["admin", "questionnaire-image-questions"] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Speichern fehlgeschlagen."),
  });

  const patch = (value: string, next: Partial<Option>) =>
    setOptions((prev) => prev.map((o) => (o.value === value ? { ...o, ...next } : o)));

  const move = (index: number, delta: number) =>
    setOptions((prev) => {
      const next = [...prev];
      const target = index + delta;
      if (target < 0 || target >= next.length) return prev;
      const a = next[index]!;
      next[index] = next[target]!;
      next[target] = a;
      return next;
    });

  const addOption = () =>
    setOptions((prev) => [
      ...prev,
      {
        value: `option-${prev.length + 1}-${Math.random().toString(36).slice(2, 6)}`,
        label: `Option ${prev.length + 1}`,
        description: null,
        image_path: null,
        sort_order: prev.length,
      },
    ]);

  const upload = async (option: Option, file: File) => {
    if (file.size > MAX_BYTES) {
      toast.error("Bitte ein Bild bis 5 MB verwenden.");
      return;
    }
    setUploading(option.value);
    try {
      const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
      const path = `${question.id}/${option.value}-${Date.now()}.${ext}`;
      const { error } = await supabase.storage
        .from(QUESTIONNAIRE_ASSET_BUCKET)
        .upload(path, file, { upsert: true, contentType: file.type });
      if (error) throw error;
      patch(option.value, { image_path: path });
      toast.success("Bild hochgeladen – bitte noch speichern.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload fehlgeschlagen.");
    } finally {
      setUploading(null);
    }
  };

  return (
    <section className="rounded-lg border border-border bg-card p-5 shadow-sm">
      <header className="mb-4">
        <p className="text-xs text-muted-foreground">{question.templateTitle}</p>
        <h2 className="font-display text-base font-extrabold text-primary">{question.label}</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {question.fieldType === "image_priority"
            ? `Bild-Priorisierung${question.topN ? ` · Top ${question.topN}` : " · Top 3"}`
            : "Bild-Bewertung"}{" "}
          · {options.length} Optionen
        </p>
      </header>

      <ul className="space-y-4">
        {options.map((o, i) => (
          <li
            key={o.value}
            className="grid gap-3 rounded-md border border-border bg-background p-3 sm:grid-cols-[8rem_1fr_auto]"
          >
            <div className="aspect-[4/3] w-full overflow-hidden rounded border border-border bg-secondary">
              {o.image_path && urls[o.image_path] ? (
                <img src={urls[o.image_path]} alt={o.label} className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center">
                  <ImageIcon className="h-5 w-5 text-muted-foreground" aria-hidden />
                </div>
              )}
            </div>

            <div className="min-w-0 space-y-2">
              <input
                type="text"
                value={o.label}
                onChange={(e) => patch(o.value, { label: e.target.value })}
                placeholder="Titel"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
              />
              <textarea
                value={o.description ?? ""}
                onChange={(e) => patch(o.value, { description: e.target.value || null })}
                rows={2}
                placeholder="Beschreibungstext (wird unter der Kachel angezeigt)"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
              />
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-border px-3 py-1.5 font-display text-xs font-extrabold text-primary hover:bg-secondary">
                {uploading === o.value ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                ) : (
                  <Upload className="h-3.5 w-3.5" aria-hidden />
                )}
                Bild wählen
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void upload(o, file);
                    e.target.value = "";
                  }}
                />
              </label>
            </div>

            <div className="flex items-start gap-1">
              <button
                type="button"
                onClick={() => move(i, -1)}
                aria-label="Nach oben"
                className="rounded-md border border-border p-1.5 text-primary hover:bg-secondary"
              >
                <ArrowUp className="h-3.5 w-3.5" aria-hidden />
              </button>
              <button
                type="button"
                onClick={() => move(i, 1)}
                aria-label="Nach unten"
                className="rounded-md border border-border p-1.5 text-primary hover:bg-secondary"
              >
                <ArrowDown className="h-3.5 w-3.5" aria-hidden />
              </button>
              <button
                type="button"
                onClick={() => setOptions((prev) => prev.filter((x) => x.value !== o.value))}
                aria-label="Option entfernen"
                className="rounded-md border border-border p-1.5 text-destructive hover:bg-secondary"
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden />
              </button>
            </div>
          </li>
        ))}
      </ul>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={addOption}
          className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 font-display text-sm font-extrabold text-primary hover:bg-secondary"
        >
          <Plus className="h-3.5 w-3.5" aria-hidden /> Option ergänzen
        </button>
        <button
          type="button"
          onClick={() => saveM.mutate(options)}
          disabled={saveM.isPending}
          className="inline-flex items-center gap-1.5 rounded-md bg-brand-button px-4 py-2 font-display text-sm font-extrabold text-foreground disabled:opacity-60"
        >
          {saveM.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />}
          Speichern
        </button>
      </div>
    </section>
  );
}
